import React, { useState } from 'react'; // NEW: Import useState
import { CostProduct, ComplexCost } from '../types/costwand.d'; // Import ComplexCost
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import Pagination from './ui/Pagination.tsx';
import { Link } from 'react-router-dom';
import { Button } from './ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx'; // Import Dialog components
import { Spinner } from './ui/spinner.tsx'; // Import Spinner
import { apiClient } from '../api/apiClient.ts'; // NEW: Import apiClient

// New component to display tiered cost details
interface TieredCostViewerProps {
  tieredCosts: ComplexCost['tiered'];
}

const TieredCostViewer: React.FC<TieredCostViewerProps> = ({ tieredCosts }) => {
  if (!tieredCosts || Object.keys(tieredCosts).length === 0) {
    return <p className="text-muted-foreground">No tiered costs available.</p>;
  }
  return (
    <ul className="list-disc list-inside space-y-1">
      {Object.entries(tieredCosts).map(([quantity, tierData]) => (
        <li key={quantity}>
          <span className="font-semibold">{quantity} units:</span>
          <br />Unit Price: ${tierData.unit_price.toFixed(4)}
          <br />Discount: {tierData.discount_perc.toFixed(2)}%
          <br />Total Value: ${tierData.tier_total_value.toFixed(4)}
        </li>
      ))}
    </ul>
  );
};


interface CostTableProps {
  costs: CostProduct[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const CostTable: React.FC<CostTableProps> = ({
  costs,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  loading,
  error,
  baseEndpoint, // NEW: Destructure baseEndpoint
}) => {
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading)
    return (
      <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading Costs...</p>
    );
  if (error)
    return (
      <p className="text-center text-destructive">Error: {error}</p>
    );
  if (costs.length === 0 && !loading)
    return (
      <p className="text-center text-muted-foreground">No Costs found.</p>
    );

  // Helper to format cost, handling both number and ComplexCost
  const formatCost = (cost: CostProduct['cost']): React.ReactNode => {
    if (typeof cost === 'number') {
      return '$' + cost.toFixed(4);
    } else if (cost && typeof cost === 'object' && 'base' in cost) { // Ensure it's ComplexCost
      return (
        <div>
          <p>Base COGS: ${cost.base.cogs_total?.toFixed(4) ?? 'N/A'}</p>
          {cost.margin && (
            <p className="text-xs text-muted-foreground">
              (Markup: {cost.margin.mark_up_perc}%, Selling Base: ${cost.margin.selling_price_base?.toFixed(4)})
            </p>
          )}
          {cost.tiered && Object.keys(cost.tiered).length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="default">
                  View Tiered Costs
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Tiered Costs</DialogTitle>
                </DialogHeader>
                <TieredCostViewer tieredCosts={cost.tiered} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      );
    }
    return 'N/A';
  };

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Article No.</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Family</TableHead>
            <TableHead>Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {costs.map((cost) => (
            <TableRow key={cost.id}>
              <TableCell>{cost.description}</TableCell>
              <TableCell>{cost.article_number}</TableCell>
              <TableCell>
                {cost._labels?.vendor_id || `ID: ${cost.vendor_id}`}
              </TableCell>
              <TableCell>
                {cost._labels?.family_id || `ID: ${cost.family_id}`}
              </TableCell>
              <TableCell>
                {formatCost(cost.cost)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default CostTable;